import { Injectable, NgZone } from '@angular/core';
import { createEffect, Actions, ofType, concatLatestFrom } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import * as SalesDocumentsActions from './sales-documents.actions';
import { IDBServiceBrowser } from '../idb.service.browser';
import {
    CacheService,
    flattenConnections,
    SalesDocumentPosition,
    Operation,
    SalesDocumentPositionBasePrices,
    SalesDocumentPositionPrices,
    SalesDocumentType,
    State,
} from '@icc/b2b/shared';
import { ConfigService, ExchangeRateService, core } from '@icc/shared';
import { Action, Store, select } from '@ngrx/store';
import { SyncService } from '../sync.service';
import { buffer, concatMap, filter, first, map, mergeMap, skipWhile, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import {
    getAllChanges,
    getBuyDocumentsForDocument,
    getBuyPositionsForPosition,
    getChangesToSave,
    getConnectionsForPositions,
    getSalesDocument,
} from './sales-documents.selectors';
import { SalesDocumentService } from '../sales-document.service';
import { PositionService } from '../position.service';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { B2bDocumentManagerService } from '../b2b-document-manager.service';

@Injectable()
export class SalesDocumentsEffects {
    private buffering = false;

    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.init),
            fetch({
                run: () => {
                    if (!this.configService.config?.userData) {
                        return;
                    }
                    this.salesDocumentService.loadData();
                    this.idbService.open(
                        this.configService.config?.userData?.user,
                        this.configService.config?.env?.iccDbVersion || 1
                    );
                    const userId = this.configService.config?.userData?.id ?? 0;
                    const machineId = this.syncService.start(userId);
                    const openedDocumentId = localStorage.getItem('openedDocument+' + userId) || null;
                    return from(this.idbService.getInitialState(openedDocumentId)).pipe(
                        switchMap(
                            async ({
                                documents,
                                documentsIds,
                                ops,
                                positions,
                                appointments,
                                complaintNotes,
                                complaintSpareParts,
                                attachments,
                                deliveries,
                                documentConnections,
                                operationsKeysMap,
                                operationsIds,
                            }) => {
                                await this.syncService.startDocumentSync(openedDocumentId);
                                await this.exchangeRateService.loaded;

                                return SalesDocumentsActions.loadSalesDocumentsSuccess({
                                    documents,
                                    documentsIds,
                                    ops,
                                    operationsKeysMap,
                                    operationsIds,
                                    positions,
                                    appointments,
                                    complaintNotes,
                                    complaintSpareParts,
                                    attachments,
                                    deliveries,
                                    documentConnections,
                                    selectedId: openedDocumentId,
                                    userId: this.configService.config?.userData?.id,
                                    userCompanyId: Number(
                                        this.configService.config?.userData?.user.ActiveCompany
                                    ),
                                    userMarketId: this.configService.config?.userData?.user?.ActiveMarket,
                                    userEmail: this.configService.config?.userData?.user.email ?? '',
                                    userPhoneNumber: this.configService.config?.userData?.phoneNumber ?? '',
                                    userReferenceName: this.configService.config?.userData?.user.person?.reference_name ?? '',
                                    machineId,
                                });
                            }
                        )
                    );
                },

                onError: (action, error) => {
                    console.error('Error', error);
                    return SalesDocumentsActions.loadSalesDocumentsFailure({ error });
                },
            })
        )
    );

    openedDocument$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SalesDocumentsActions.openDoc),
                tap((payload) => {
                    const userId = this.configService.config?.userData?.id;
                    if (payload.id) {
                        localStorage.setItem('openedDocument+' + userId, payload.id);
                    } else {
                        localStorage.removeItem('openedDocument+' + userId);
                    }
                })
            ),
        { dispatch: false }
    );

    syncBuyDocumentWithSalesAddPosition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addPosition),
            filter((action) => !action.onlySales && action.position.type !== 'defect'),
            concatMap((action) =>
                from(
                    (async () => {
                        // Read store state lazily inside concatMap so that each
                        // queued action sees the effects of the previous one
                        // (newly created buy documents and connections).
                        // Using concatLatestFrom here would capture the snapshot
                        // at action emission time, causing duplicate buy
                        // documents to be created for the same department when
                        // multiple positions are added in quick succession
                        // (e.g. "Dobór do kompletu").
                        const salesDocument = await this.store
                            .select(getSalesDocument(action.position.documentId))
                            .pipe(first())
                            .toPromise();
                        if (
                            salesDocument == null ||
                            Number(salesDocument.sellerCompanyId) !==
                                Number(this.configService.user.user.ActiveCompany) ||
                            action.position.type === 'additional' ||
                            this.configService.user.user.ActiveCompanyLicenseType === 'license' ||
                            salesDocument.type !== 'sales' ||
                            salesDocument.salesDocumentTypeId === SalesDocumentType.ServiceOrder
                        ) {
                            return [] as Action[];
                        }
                        const documents = await this.store
                            .select(getBuyDocumentsForDocument(action.position.documentId))
                            .pipe(first())
                            .toPromise() ?? [];
                        const ops: Action[] = [];
                        let buyDocument = documents.find(
                            (doc) =>
                                Number(doc.sellerProductDepartmentId) ===
                                    Number(action.position.department?.id) ||
                                (!doc.sellerProductDepartmentId && !action.position.department?.id)
                        );

                        const newBuyDocument = !buyDocument;

                        if (!buyDocument) {
                            buyDocument =
                                this.salesDocumentService.createBuyDocumentFromSalesDocument(
                                    salesDocument,
                                    action.position.department?.id
                                );
                            ops.push(
                                SalesDocumentsActions.addDoc({
                                    doc: buyDocument,
                                }),
                                SalesDocumentsActions.joinDocuments({
                                    uuid: core.generateUUID(),
                                    salesDocumentId: salesDocument.uuid,
                                    buyDocumentId: buyDocument.uuid,
                                })
                            );
                        }

                        const currentOfferValidity =
                            this.offerValidityService.getCurrentOfferValidity(buyDocument);
                        const documentPriceVersion = this.priceVersionService.getPriceVersion(
                            buyDocument.priceVersionId
                        );
                        const buyPositionsMap = await this.b2bDocumentManagerService.getBuyPositionsMap(salesDocument.uuid);
                        const position =
                            await this.positionService.createBuyPositionFromSalesPosition(
                                action.position as SalesDocumentPosition,
                                buyDocument,
                                documentPriceVersion,
                                currentOfferValidity,
                                buyPositionsMap,
                            );
                        ops.push(
                            SalesDocumentsActions.addPosition({
                                position,
                                onlySales: true,
                            }),
                            SalesDocumentsActions.joinPositions({
                                uuid: core.generateUUID(),
                                salesDocumentId: action.position.documentId,
                                salesPositionId: action.position.uuid,
                                buyDocumentId: position.documentId,
                                buyPositionId: position.uuid,
                            })
                        );

                        if (newBuyDocument) {
                            const attachments = await this.b2bDocumentManagerService.getDocumentAttachments(salesDocument.uuid);
                            for (const attachment of attachments) {
                                const buyPositionForAttachment = buyPositionsMap.get(attachment.documentPositionId);
                                if (!attachment.documentPositionId || buyPositionForAttachment?.documentId === buyDocument.uuid) {
                                    const buyAttachmentId = core.generateUUID();
                                    ops.push(
                                        SalesDocumentsActions.addAttachment({
                                            attachment: {
                                                ...attachment,
                                                uuid: buyAttachmentId,
                                                documentId: buyDocument.uuid,
                                                documentPositionId: buyPositionForAttachment?.uuid,
                                            },
                                        }),
                                        SalesDocumentsActions.joinAttachments({
                                            uuid: core.generateUUID(),
                                            salesDocumentId: salesDocument.uuid,
                                            buyDocumentId: buyDocument.uuid,
                                            salesAttachmentId: attachment.uuid,
                                            buyAttachmentId,
                                        })
                                    );

                                    if (attachment.data) {
                                        CacheService.saveFile(buyAttachmentId, attachment.data);
                                    }
                                }
                            }
                        }
                        return ops;
                    })()
                )
            ),
            concatMap((ops) => ops),
        )
    );

    syncBuyDocumentWithSalesChangePosition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SalesDocumentsActions.changePosition,
                SalesDocumentsActions.changePositionBasePrice
            ),
            filter((action) => !action.onlySales),
            concatLatestFrom((action) => this.store.select(getBuyPositionsForPosition(action.id))),
            filter(([, documents]) => documents.length > 0),
            concatLatestFrom(([, documents]) =>
                this.store.select(getSalesDocument(documents[0].documentId)).pipe(first())
            ),
            concatMap(([[action, documents], buyDocument]) =>
                from(
                    (async () => {
                        const actions: Action[] = [];
                        const buyPositionMap = await this.b2bDocumentManagerService.getBuyPositionsMap(action.docId);
                        for (const position of documents) {
                            const currentOfferValidity =
                                this.offerValidityService.getCurrentOfferValidity(buyDocument);
                            const documentPriceVersion = this.priceVersionService.getPriceVersion(
                                buyDocument.priceVersionId
                            );
                            const changes =
                                await this.positionService.changesForBuyPositionFromSalesPosition(
                                    buyDocument,
                                    position,
                                    {
                                        ...action.changes as SalesDocumentPosition,
                                    },
                                    documentPriceVersion,
                                    currentOfferValidity,
                                    buyPositionMap
                                );

                            if ('basePriceValue' in changes) {
                                const priceChanges: SalesDocumentPositionBasePrices = {};
                                const positionPricesKeys = [
                                    'basePriceValue',
                                    'basePriceCurrency',
                                    'basePriceTaxRate',
                                    'basePriceTaxRateId',
                                    'basePriceIsGross',
                                    'baseBuyPriceValue',
                                    'baseBuyPriceCurrency',
                                    'baseBuyPriceTaxRate',
                                    'baseBuyPriceTaxRateId',
                                    'baseBuyPriceIsGross',
                                ] as const;
                                for (const key of positionPricesKeys) {
                                    if (key in changes) {
                                        (priceChanges[key] as any) = changes[key];
                                    }
                                }
                                if (Object.keys(priceChanges).length > 0) {
                                    actions.push(
                                        SalesDocumentsActions.changePositionBasePrice({
                                            changes: priceChanges,
                                            docId: position.documentId,
                                            id: position.uuid,
                                            onlySales: true,
                                        })
                                    );
                                }
                                for (const key in priceChanges) {
                                    if (Object.prototype.hasOwnProperty.call(priceChanges, key)) {
                                        delete changes[key];
                                    }
                                }
                            }
                            if ('priceBeforeDiscountsValue' in changes) {
                                const priceChanges: SalesDocumentPositionPrices = {};
                                const positionPricesKeys = [
                                    'pricesRevisions',
                                    'priceCalculationDate',
                                    'priceBeforeDiscountsValue',
                                    'priceAfterDiscountsValue',
                                    'priceDiscounts',
                                    'priceCurrency',
                                    'priceTaxRate',
                                    'priceTaxRateId',
                                    'priceIsGross',
                                    'validityDate',
                                ] as const;
                                for (const key of positionPricesKeys) {
                                    if (key in changes) {
                                        (priceChanges[key] as any) = changes[key];
                                    }
                                }
                                if (Object.keys(priceChanges).length > 0) {
                                    actions.push(
                                        SalesDocumentsActions.changePositionPrice({
                                            changes: priceChanges,
                                            docId: position.documentId,
                                            id: position.uuid,
                                        })
                                    );
                                }
                                for (const key in priceChanges) {
                                    if (Object.prototype.hasOwnProperty.call(priceChanges, key)) {
                                        delete changes[key];
                                    }
                                }
                            }
                            actions.push(
                                SalesDocumentsActions.changePosition({
                                    changes,
                                    docId: position.documentId,
                                    id: position.uuid,
                                    onlySales: true,
                                })
                            );
                        }
                        return actions;
                    })()
                )
            ),
            concatMap((ops) => ops)
        )
    );

    syncBuyDocumentWithSalesChangeDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changeDoc),
            filter((action) => !action.onlySales),
            concatLatestFrom((action) => this.store.select(getBuyDocumentsForDocument(action.id))),
            filter(([, documents]) => documents.length > 0),
            switchMap(([action, documents]) =>
                documents.map((doc) =>
                    SalesDocumentsActions.changeDoc({
                        changes: action.changes,
                        id: doc.uuid,
                    })
                )
            )
        )
    );

    syncBuyDocumentWithSalesChangePositionSlaves$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SalesDocumentsActions.changePosition,
            ),
            filter((action) => !action.onlySales && 'slaves' in action.changes),
            concatMap((action) => {
                const slavePositionIds: string[] = [];
                for (const fieldId of Object.keys(action.changes.slaves || {})) {
                    slavePositionIds.push(action.changes.slaves[fieldId].positionId);
                }
                return this.store.pipe(
                    select(getConnectionsForPositions(slavePositionIds)),
                    skipWhile((connections) => connections.length < slavePositionIds.length),
                    withLatestFrom(of(action)),
                    first(),
                )
            }),
            map(([, action]) => action),
            concatLatestFrom((action) => this.store.select(getBuyPositionsForPosition(action.id))),
            filter(([, documents]) => documents.length > 0),
            concatLatestFrom(([, documents]) =>
                this.store.select(getSalesDocument(documents[0].documentId)).pipe(first())
            ),
            map(([[action, buyPositions], buyDocument]) => SalesDocumentsActions.changePositionSlaves({
                id: action.id,
                docId: action.docId,
                slaves: action.changes.slaves,
                buyPositions,
                buyDocument,
            }))
        )
    );

    syncBuyPositionSlavesWithSales$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SalesDocumentsActions.changePositionSlaves,
            ),
            concatMap(({slaves, buyPositions, buyDocument, docId, id}) =>
                from(
                    (async () => {
                        const actions: Action[] = [];
                        const buyPositionMap = await this.b2bDocumentManagerService.getBuyPositionsMap(docId);
                        for (const position of buyPositions) {
                            const buySlaves = this.positionService.updateSlaves(slaves, position, buyPositionMap);
                            position.slaves = buySlaves;
                            actions.push(
                                SalesDocumentsActions.changePosition({
                                    changes: {
                                        slaves: buySlaves
                                    },
                                    docId: position.documentId,
                                    id: position.uuid,
                                    onlySales: true,
                                })
                            );
                        }
                        return actions;
                    })()
                )
            ),
            concatMap((ops) => ops)
        )
    );

    saveDocs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.mergeOps),
            // Skip IDB save and worker send for operations from BroadcastChannel
            // These are already saved/synced by the originating tab
            filter((action) => !action.fromBroadcast),
            concatLatestFrom(() => this.store.select(getAllChanges)),
            concatMap(([action, changes]) => {
                if (this.buffering) {
                    return [SalesDocumentsActions.noop()];
                } else {
                    return this.processActions(of([action]), changes);
                }
            }),
        )
    );

    startBuffering$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.startBuffering),
            tap(() => {
                this.buffering = true;
            }),
            switchMap(() => this.actions$.pipe(
                ofType(SalesDocumentsActions.mergeOps),
                // Skip operations from BroadcastChannel - they don't need to be saved/sent
                filter((action) => !action.fromBroadcast),
                concatLatestFrom(() => this.store.select(getChangesToSave)),
                buffer(this.actions$.pipe(ofType(SalesDocumentsActions.stopBuffering))),
                tap(() => {
                    this.buffering = false;
                }),
                concatLatestFrom(() => this.store.select(getAllChanges)),
                concatMap(([actions, changes]) => this.processBufferedActions(actions, changes))
            ))
        )
    );

    sendOpsToWorker$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SalesDocumentsActions.sendOpsToWorker),
                map((action) => {
                    if (action.syncKey) {
                        const documentIds = new Set<string>();
                        for (const op of action.ops) {
                            documentIds.add(op.docId);
                            if ('buyDocId' in op && op.buyDocId) {
                                documentIds.add(op.buyDocId);
                            }
                        }
                        this.syncService.sendProcessResultsToWorker(Array.from(documentIds), action.syncKey);
                    } else {
                        this.syncService.sendOperationsToWorker({
                            ops: action.ops,
                            staleDocs: action.staleDocs,
                            graphsToSend: action.graphsToSend,
                        });
                    }
                })
            ),
        {
            dispatch: false,
        }
    );

    updatedSyncedStateOnWorker$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.updatedSyncedStateOnWorker),
            map((action) => {
                if (action.changedDocs?.length) {
                    this.syncService.loadDocuments(this.syncService.currentDocumentsIds);
                }
            })
        ),
        { dispatch: false }
    );

    private processActions(actions$: Observable<any[]>, allDocs) {
        return actions$.pipe(
            concatMap((actions) =>
                from(
                    (async () => {
                        await this.ngZone.runOutsideAngular(async () => {
                            const dirtyDocs = allDocs.documents.filter((el) => el.$dirty);
                            if (dirtyDocs.length) {
                                await this.idbService.saveMany('docs', dirtyDocs);
                            }
                            const dirtyPositions = allDocs.positions.filter((el) => el.$dirty);
                            if (dirtyPositions.length) {
                                await this.idbService.saveMany('positions', dirtyPositions);
                            }
                            const dirtyAttachments = allDocs.attachments.filter((el) => el.$dirty);
                            if (dirtyAttachments.length) {
                                await this.idbService.saveMany('attachments', dirtyAttachments);
                            }
                            const dirtyDeliveries = allDocs.deliveries.filter((el) => el.$dirty);
                            if (dirtyDeliveries.length) {
                                await this.idbService.saveMany('deliveries', dirtyDeliveries);
                            }
                            const dirtyAppointments = allDocs.appointments.filter((el) => el.$dirty);
                            if (dirtyAppointments.length) {
                                await this.idbService.saveMany('appointments', dirtyAppointments);
                            }
                            const dirtyComplaintNotes = allDocs.complaintNotes.filter((el) => el.$dirty);
                            if (dirtyComplaintNotes.length) {
                                await this.idbService.saveMany('complaint_notes', dirtyComplaintNotes);
                            }
                            const dirtyComplaintSpareParts = allDocs.complaintSpareParts.filter((el) => el.$dirty);
                            if (dirtyComplaintSpareParts.length) {
                                await this.idbService.saveMany('complaint_spare_parts', dirtyComplaintSpareParts);
                            }
                            const dirtyDocumentConnections = flattenConnections(
                                allDocs.documentConnections
                            ).filter((el) => el.$dirty);
                            if (dirtyDocumentConnections.length) {
                                await this.idbService.saveMany(
                                    'document_connections',
                                    dirtyDocumentConnections
                                );
                            }
                            await this.idbService.saveMany('ops', allDocs.opsToSave);
                        });
                        return {
                            opsToSendToWorker: allDocs.opsToSendToWorker,
                            staleDocs: allDocs.staleDocs,
                            graphsToSend: allDocs.graphsToSend,
                            syncKey: actions.filter(action => action.syncKey).map(action => action.syncKey)[0],
                            savedTags: allDocs.opsToSave.filter(op => op.type === 'tagDoc').map(op => op.changes),
                        };
                    })()
                )
            ),
            mergeMap((result) => ([
                SalesDocumentsActions.tagsSavedInDb({
                    savedTags: result.savedTags,
                }),
                SalesDocumentsActions.sendOpsToWorker({
                    ops: result.opsToSendToWorker,
                    staleDocs: result.staleDocs,
                    graphsToSend: result.graphsToSend,
                    syncKey: result.syncKey,
                })
            ]))
        );
    }

    private processBufferedActions(bufferedActions: [any, {
        opsToSave: Operation[],
        opsToSendToWorker: Operation[],
        graphsToSend: Map<string, number>,
    }][], changes) {
        const actions = bufferedActions.map(([action]) => action);
        const opsToSave = [];
        const opsToSendToWorker = [];
        const graphsToSend = new Map<string, number>();

        bufferedActions.forEach(([, ops]) => {
            opsToSave.push(...ops.opsToSave);
            opsToSendToWorker.push(...ops.opsToSendToWorker);
            for (const [docId, graph] of ops.graphsToSend) {
                graphsToSend.set(docId, graph);
            }
        });
        changes.opsToSave = opsToSave;
        changes.opsToSendToWorker = opsToSendToWorker;
        changes.graphsToSend = graphsToSend;
        return this.processActions(of(actions), changes);
    }

    constructor(
        private actions$: Actions,
        private idbService: IDBServiceBrowser,
        private store: Store<State>,
        private syncService: SyncService,
        private configService: ConfigService,
        private salesDocumentService: SalesDocumentService,
        private b2bDocumentManagerService: B2bDocumentManagerService,
        private positionService: PositionService,
        private offerValidityService: OfferValidityService,
        private priceVersionService: PriceVersionService,
        private ngZone: NgZone,
        private exchangeRateService: ExchangeRateService,
    ) {}
}
